import { supabase, TABLES } from '../supabase'
import { cacheService } from './cacheService'

// Testar conexão ao inicializar
const testSupabaseConnection = async () => {
  try {
    const { error } = await supabase.from('users').select('count', { count: 'exact', head: true }).limit(1);
    if (error) {
      return false;
    }
    return true;
  } catch (err) {
    return false;
  }
};

testSupabaseConnection();

// 🔥 SERVIÇOS DE USUÁRIO
export const userService = {
  // Criar usuário
  async createUser(userData) {
    try {
      // Remover campos que podem causar conflito
      const { created_at, ...validData } = userData;
      
      const { data, error } = await supabase
        .from(TABLES.USERS)
        .insert([validData])
        .select()
      
      if (error) {
        throw error;
      }
      
      return data[0]
    } catch (err) {
      throw err;
    }
  },

  // Buscar usuário por ID (COM CACHE)
  async getUserById(userId) {
    try {
      // Tentar buscar do cache primeiro
      const cached = cacheService.get(`user_${userId}`);
      if (cached) {
        console.log('✅ Cache HIT: user', userId);
        return cached;
      }

      console.log('🔍 Cache MISS: buscando user', userId);
      const { data, error } = await supabase
        .from(TABLES.USERS)
        .select('*')
        .eq('id', userId)
        .single()
      
      if (error) {
        throw error;
      }
      
      // Salvar no cache
      if (data) {
        cacheService.set(`user_${userId}`, data);
      }
      
      return data
    } catch (err) {
      throw err;
    }
  },

  // Buscar usuário atual (logado)
  async getUser() {
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        throw authError || new Error('Usuário não autenticado');
      }
      
      return await this.getUserById(user.id);
    } catch (err) {
      throw err;
    }
  },

  // Buscar todos os usuários
  async getAllUsers() {
    try {
      const { data, error } = await supabase
        .from(TABLES.USERS)
        .select('*')
        .order('created_at', { ascending: false })
      
      if (error) {
        throw error;
      }
      
      return data || []
    } catch (err) {
      throw err; // Não usar fallback, lançar o erro real
    }
  },

  // Atualizar usuário
  async updateUser(userId, updates) {
    try {
      console.log('🔄 Atualizando usuário:', { userId, campos: Object.keys(updates) });
      
      // Se está atualizando avatar, mostrar tamanho
      if (updates.avatar) {
        console.log('📸 Tamanho do avatar:', (updates.avatar.length / 1024 / 1024).toFixed(2), 'MB');
      }
      
      // Lista de campos válidos na tabela users do Supabase (TODOS OS CAMPOS)
      const validFields = [
        'nome', 'email', 'status', 'cidade', 'curso', 'periodo', 'avatar',
        'universidade', 'quemsoueu', 'etnia', 'humor', 'interesses', 
        'aniversario', 'idiomas', 'moro', 'verified'
      ];
      
      // Filtrar apenas campos válidos
      const validUpdates = {};
      Object.keys(updates).forEach(key => {
        if (validFields.includes(key)) {
          validUpdates[key] = updates[key];
        } else {
          console.log('⚠️ Campo ignorado (não existe no BD):', key);
        }
      });
      
      // Adicionar timestamp de atualização
      validUpdates.updated_at = new Date().toISOString();
      
      console.log('📤 Campos sendo enviados:', Object.keys(validUpdates));
      
      const { data, error } = await supabase
        .from(TABLES.USERS)
        .update(validUpdates)
        .eq('id', userId)
        .select()
      
      if (error) {
        throw error;
      }
      
      return data[0]
    } catch (err) {
      throw err;
    }
  },

  // Buscar amigos
  async getFriends(userId) {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .select('id, friend_id, created_at')
        .eq('user_id', userId)
        .eq('status', 'accepted');
      
      if (error) {
        throw error;
      }
      
      // Buscar dados de cada amigo
      const friendsData = await Promise.all(
        (data || []).map(async (friendship) => {
          const friend = await this.getUserById(friendship.friend_id);
          return {
            ...friend,
            friendship_created_at: friendship.created_at // Data de quando se tornaram amigos
          };
        })
      );
      
      const validFriends = friendsData.filter(f => f !== null);
      return validFriends;
    } catch (err) {
      return [];
    }
  },

  // Adicionar amigo (aceitar solicitação)
  async addFriend(userId, friendId) {
    try {
      // Criar amizade bidirecional
      const { error: error1 } = await supabase
        .from('friendships')
        .insert([
          { user_id: userId, friend_id: friendId, status: 'accepted', created_at: new Date().toISOString() }
        ]);
      
      const { error: error2 } = await supabase
        .from('friendships')
        .insert([
          { user_id: friendId, friend_id: userId, status: 'accepted', created_at: new Date().toISOString() }
        ]);
      
      if (error1 || error2) throw error1 || error2;
      return true;
    } catch (err) {
      return false;
    }
  },

  // Enviar solicitação de amizade
  async sendFriendRequest(fromUserId, toUserId) {
    try {
      const { error } = await supabase
        .from('friendships')
        .insert([
          { user_id: fromUserId, friend_id: toUserId, status: 'pending', created_at: new Date().toISOString() }
        ]);
      
      if (error) throw error;
      return true;
    } catch (err) {
      return false;
    }
  },

  // Verificar se já são amigos
  async checkFriendship(userId, friendId) {
    try {
      const { data, error } = await supabase
        .from('friendships')
        .select('status')
        .eq('user_id', userId)
        .eq('friend_id', friendId)
        .single();
      
      if (error) return null;
      return data?.status;
    } catch (err) {
      return null;
    }
  },

  // Cancelar/Remover solicitação
  async removeFriendship(userId, friendId) {
    try {
      // Remove amizade bidirecional
      await supabase
        .from('friendships')
        .delete()
        .eq('user_id', userId)
        .eq('friend_id', friendId);
      
      await supabase
        .from('friendships')
        .delete()
        .eq('user_id', friendId)
        .eq('friend_id', userId);
      
      return true;
    } catch (err) {
      return false;
    }
  },

  // Aceitar solicitação de amizade
  async acceptFriendRequest(userId, friendId) {
    try {
      // Primeiro, atualizar o registro pendente para 'accepted'
      const { error: updateError } = await supabase
        .from('friendships')
        .update({ status: 'accepted' })
        .eq('user_id', friendId)
        .eq('friend_id', userId)
        .eq('status', 'pending');
      
      if (updateError) {
        throw updateError;
      }
      
      // Verificar se já existe o registro reverso
      const { data: existingReverse } = await supabase
        .from('friendships')
        .select('id')
        .eq('user_id', userId)
        .eq('friend_id', friendId)
        .single();
      
      // Se não existe registro reverso, criar
      if (!existingReverse) {
        const { error: insertError } = await supabase
          .from('friendships')
          .insert([
            {
              user_id: userId,
              friend_id: friendId,
              status: 'accepted',
              created_at: new Date().toISOString()
            }
          ]);
        
        if (insertError) {
          throw insertError;
        }
      } else {
        // Se existe, apenas atualizar para 'accepted'
        const { error: updateReverseError } = await supabase
          .from('friendships')
          .update({ status: 'accepted' })
          .eq('user_id', userId)
          .eq('friend_id', friendId);
        
        if (updateReverseError) {
          throw updateReverseError;
        }
      }
      
      return true;
    } catch (err) {
      return false;
    }
  },

  // Contar amigos
  async getFriendsCount(userId) {
    try {
      const { count, error } = await supabase
        .from('friendships')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('status', 'accepted');
      
      if (error) throw error;
      return count || 0;
    } catch (err) {
      return 0;
    }
  }
}

// 🎨 SERVIÇOS DE ELOGIOS
export const elogioService = {
  // Enviar elogio
  async sendElogio(fromUserId, toUserId, elogioType) {
    try {
      const { data, error } = await supabase
        .from('elogios')
        .insert([
          { 
            from_user_id: fromUserId, 
            to_user_id: toUserId, 
            tipo: elogioType,
            created_at: new Date().toISOString()
          }
        ])
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      throw err;
    }
  },

  // Buscar elogios de um usuário
  async getUserElogios(userId) {
    try {
      const { data, error } = await supabase
        .from('elogios')
        .select('tipo')
        .eq('to_user_id', userId);
      
      if (error) throw error;
      
      // Contar elogios por tipo
      const elogios = {
        legal: 0,
        confiavel: 0,
        criativo: 0,
        inteligente: 0,
        simpatico: 0
      };
      
      data?.forEach(elogio => {
        if (elogios.hasOwnProperty(elogio.tipo)) {
          elogios[elogio.tipo]++;
        }
      });
      
      return elogios;
    } catch (err) {
      return { legal: 0, confiavel: 0, criativo: 0, inteligente: 0, simpatico: 0 };
    }
  },

  // Verificar se já elogiou hoje
  async checkCanSendElogio(fromUserId, toUserId, elogioType) {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const { data, error } = await supabase
        .from('elogios')
        .select('id')
        .eq('from_user_id', fromUserId)
        .eq('to_user_id', toUserId)
        .eq('tipo', elogioType)
        .gte('created_at', today.toISOString())
        .single();
      
      // Se não encontrou, pode enviar
      return !data;
    } catch (err) {
      // Se deu erro (não encontrou), pode enviar
      return true;
    }
  }
}

// 📝 SERVIÇOS DE POSTS COM FALLBACKS
export const postService = {
  // Criar post
  async createPost(postData) {
    try {
      // Verificar autenticação
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        throw new Error('Usuário não autenticado');
      }
      
      const { data, error } = await supabase
        .from(TABLES.POSTS)
        .insert([postData])
        .select()
      
      if (error) {
        throw error;
      }
      
      // Invalidar cache de posts ao criar novo post
      cacheService.invalidate('posts_recent');
      console.log('🔄 Cache de posts invalidado após criar novo post');
      
      return data[0]
    } catch (err) {
      throw err;
    }
  },

  // Buscar todos os posts (OTIMIZADO COM LIMIT E CACHE)
  async getAllPosts(limit = 20, offset = 0) {
    try {
      // DESABILITADO: Cache causava problema com comentários
      // if (offset === 0) {
      //   const cached = cacheService.get('posts_recent');
      //   if (cached) {
      //     console.log('✅ Cache HIT: posts recentes');
      //     return cached;
      //   }
      // }

      console.log(`📚 Buscando ${limit} posts (offset: ${offset})...`);
      
      // Buscar apenas últimos 20 posts (reduz 80% do tráfego)
      const { data, error } = await supabase
        .from(TABLES.POSTS)
        .select(`
          id,
          conteudo,
          foto,
          video,
          created_at,
          user_id,
          likes_count,
          users!inner (
            nome,
            avatar,
            universidade,
            verified
          )
        `)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1)
      
      if (error) {
        throw error;
      }
      
      // Buscar comentários para todos os posts
      const postIds = (data || []).map(p => p.id);
      let commentsData = [];
      
      if (postIds.length > 0) {
        console.log('📝 Buscando comentários para', postIds.length, 'posts...');
        const { data: comments, error: commentsError } = await supabase
          .from('comments')
          .select(`
            id,
            post_id,
            user_id,
            conteudo,
            created_at,
            users (
              nome,
              avatar,
              verified
            )
          `)
          .in('post_id', postIds)
          .order('created_at', { ascending: true });
        
        if (!commentsError && comments) {
          commentsData = comments;
          console.log('✅ Comentários carregados:', comments.length);
        } else if (commentsError) {
          console.error('❌ Erro ao buscar comentários:', commentsError);
        }
      }
      
      // Organizar comentários por post
      const commentsByPost = {};
      commentsData.forEach(comment => {
        if (!commentsByPost[comment.post_id]) {
          commentsByPost[comment.post_id] = {};
        }
        commentsByPost[comment.post_id][comment.id] = {
          text: comment.conteudo,
          authorId: comment.user_id,
          authorName: comment.users?.nome || 'Usuário',
          authorPhoto: comment.users?.avatar || '',
          authorVerified: comment.users?.verified || false,
          createdAt: comment.created_at
        };
      });
      
      // Transformar dados para formato esperado (COM COMENTÁRIOS)
      const transformedPosts = (data || []).map(post => ({
        ...post,
        authorId: post.user_id,
        authorName: post.users?.nome || 'Usuário',
        authorPhoto: post.users?.avatar || '',
        authorUniversity: post.users?.universidade || '',
        authorVerified: post.users?.verified || false,
        text: post.conteudo,
        postPhoto: post.foto,
        videoUrl: post.video,
        createdAt: post.created_at,
        comments: commentsByPost[post.id] || {},
        comments_count: Object.keys(commentsByPost[post.id] || {}).length
      }));
      
      // DESABILITADO: Cache desativado para sempre buscar dados atualizados
      // if (offset === 0 && transformedPosts.length > 0) {
      //   cacheService.set('posts_recent', transformedPosts);
      // }
      
      console.log(`✅ ${transformedPosts.length} posts carregados com comentários`);
      return transformedPosts;
    } catch (err) {
      throw err; // Não usar fallback, lançar o erro real
    }
  },

  // Buscar posts arquivados (mais de 24h) - para sistema de busca
  async searchArchivedPosts(searchTerm) {
    try {
      // Calcular data de 24 horas atrás
      const twentyFourHoursAgo = new Date();
      twentyFourHoursAgo.setHours(twentyFourHoursAgo.getHours() - 24);
      
      // Buscar posts arquivados por conteúdo
      const { data: postsByContent, error: errorContent } = await supabase
        .from(TABLES.POSTS)
        .select(`
          *,
          users (
            nome,
            avatar,
            universidade,
            verified
          ),
          comments (
            id,
            conteudo,
            created_at,
            user:user_id (
              id,
              nome,
              avatar,
              verified
            )
          )
        `)
        .lt('created_at', twentyFourHoursAgo.toISOString())
        .ilike('conteudo', `%${searchTerm}%`)
        .order('created_at', { ascending: false })
        .limit(30);
      
      // Buscar posts arquivados por nome do autor
      const { data: postsByAuthor, error: errorAuthor } = await supabase
        .from(TABLES.POSTS)
        .select(`
          *,
          users!inner (
            nome,
            avatar,
            universidade,
            verified
          ),
          comments (
            id,
            conteudo,
            created_at,
            user:user_id (
              id,
              nome,
              avatar,
              verified
            )
          )
        `)
        .lt('created_at', twentyFourHoursAgo.toISOString())
        .ilike('users.nome', `%${searchTerm}%`)
        .order('created_at', { ascending: false })
        .limit(30);
      
      if (errorContent || errorAuthor) {
        throw errorContent || errorAuthor;
      }
      
      // Combinar e remover duplicatas
      const combinedData = [...(postsByContent || []), ...(postsByAuthor || [])];
      const uniqueData = combinedData.filter((post, index, self) =>
        index === self.findIndex(p => p.id === post.id)
      );
      
      const data = uniqueData.slice(0, 50); // Limitar para não trazer muitos resultados
      
      // Transformar dados (mesmo formato de getAllPosts)
      const transformedPosts = (data || []).map(post => {
        const commentsObj = {};
        if (post.comments && Array.isArray(post.comments)) {
          post.comments.forEach(comment => {
            commentsObj[comment.id] = {
              text: comment.conteudo,
              authorId: comment.user_id,
              authorName: comment.user?.nome || 'Usuário',
              authorPhoto: comment.user?.avatar || '',
              authorVerified: comment.user?.verified || false,
              createdAt: comment.created_at
            };
          });
        }
        
        return {
          ...post,
          authorId: post.user_id,
          authorName: post.users?.nome || 'Usuário',
          authorPhoto: post.users?.avatar || '',
          authorUniversity: post.users?.universidade || '',
          authorVerified: post.users?.verified || false,
          text: post.conteudo,
          postPhoto: post.foto,
          videoUrl: post.video,
          createdAt: post.created_at,
          comments: commentsObj,
          comments_count: post.comments?.length || 0,
          isArchived: true // Marcar como arquivado
        };
      });
      
      return transformedPosts;
    } catch (err) {
      return [];
    }
  },

  // Deletar post
  async deletePost(postId, userId) {
    try {
      const { error } = await supabase
        .from(TABLES.POSTS)
        .delete()
        .eq('id', postId)
        .eq('user_id', userId) // só pode deletar próprios posts
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (err) {
      throw err; // Não usar fallback, lançar o erro real
    }
  }
}

// 💬 SERVIÇOS DE COMENTÁRIOS COM FALLBACKS
export const commentService = {
  // Adicionar comentário
  async addComment(commentData) {
    try {
      const { data, error } = await supabase
        .from(TABLES.COMMENTS)
        .insert([commentData])
        .select()
      
      if (error) {
        throw error;
      }
      
      return data[0]
    } catch (err) {
      throw err; // Não usar fallback, lançar o erro real
    }
  },

  // Deletar comentário
  async deleteComment(commentId) {
    try {
      const { error } = await supabase
        .from(TABLES.COMMENTS)
        .delete()
        .eq('id', commentId)
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (err) {
      throw err;
    }
  }
}

// 👍 SERVIÇOS DE LIKES COM FALLBACKS
export const likeService = {
  // Adicionar like
  async addLike(postId, userId) {
    try {
      console.log('🔵 likeService.addLike:', { postId, userId });
      const { data, error } = await supabase
        .from(TABLES.LIKES)
        .insert([{ post_id: postId, user_id: userId }])
        .select()
      
      if (error) {
        console.error('❌ Erro ao adicionar like:', error);
        throw error;
      }
      
      console.log('✅ Like adicionado no DB:', data[0]);
      return data[0]
    } catch (err) {
      console.error('❌ Exceção em addLike:', err);
      throw err;
    }
  },

  // Remover like
  async removeLike(postId, userId) {
    try {
      console.log('🔵 likeService.removeLike:', { postId, userId });
      const { error } = await supabase
        .from(TABLES.LIKES)
        .delete()
        .eq('post_id', postId)
        .eq('user_id', userId)
      
      if (error) {
        console.error('❌ Erro ao remover like:', error);
        throw error;
      }
      
      console.log('✅ Like removido do DB');
      return true
    } catch (err) {
      console.error('❌ Exceção em removeLike:', err);
      throw err;
    }
  },

  // Verificar se usuário curtiu o post
  async checkUserLike(postId, userId) {
    try {
      const { data, error } = await supabase
        .from(TABLES.LIKES)
        .select('id')
        .eq('post_id', postId)
        .eq('user_id', userId)
        .single()
      
      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        throw error;
      }
      
      return !!data;
    } catch (err) {
      return false;
    }
  },

  // Buscar likes de um post
  async getPostLikes(postId) {
    try {
      const { data, error } = await supabase
        .from(TABLES.LIKES)
        .select('user_id')
        .eq('post_id', postId)
      
      if (error) throw error;
      return data || [];
    } catch (err) {
      return [];
    }
  }
}

// 💬 SERVIÇOS DE CHAT COM FALLBACKS
export const chatService = {
  // Enviar mensagem
  async sendMessage(messageData) {
    try {
      const { data, error } = await supabase
        .from(TABLES.MESSAGES)
        .insert([messageData])
        .select()
      
      if (error) {
        throw error;
      }
      
      return data[0]
    } catch (err) {
      return { id: 'temp_' + Date.now(), ...messageData, created_at: new Date().toISOString() }
    }
  },

  // Buscar mensagens de um chat
  async getMessages(chatId) {
    try {
      const { data, error } = await supabase
        .from(TABLES.MESSAGES)
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true })
      
      if (error) {
        throw error;
      }
      
      return data || []
    } catch (err) {
      return []
    }
  },

  // Marcar mensagem como lida
  async markMessageAsRead(messageId) {
    try {
      const { error } = await supabase
        .from(TABLES.MESSAGES)
        .update({ read: true })
        .eq('id', messageId)
      
      if (error) throw error
      return true
    } catch (err) {
      return false
    }
  },

  // Deletar mensagem
  async deleteMessage(messageId) {
    try {
      const { data, error } = await supabase
        .from(TABLES.MESSAGES)
        .delete()
        .eq('id', messageId)
        .select(); // Retorna os registros deletados
      
      if (error) {
        throw error;
      }
      
      return true;
    } catch (err) {
      return false;
    }
  },
}

// Removido - chatService já existe mais abaixo

// �🔄 SERVIÇOS DE TEMPO REAL COM FALLBACKS
export const realtimeService = {
  // Subscrever a mensagens
  subscribeToMessages(chatId, callback) {
    try {
      const subscription = supabase
        .channel(`chat-${chatId}`)
        .on('postgres_changes', {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`
        }, (payload) => callback({ ...payload, eventType: 'INSERT' }))
        .on('postgres_changes', {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `chat_id=eq.${chatId}`
        }, (payload) => callback({ ...payload, eventType: 'UPDATE' }))
        .subscribe()
      
      return subscription
    } catch (err) {
      return null
    }
  }
}

// 🎓 SERVIÇOS DE UNIVERSIDADES
export const universityService = {
  // Buscar todas as universidades
  async getAllUniversities() {
    try {
      const { data, error } = await supabase
        .from('universidades')
        .select('*')
        .order('nome', { ascending: true });
      
      if (error) throw error;
      return data;
    } catch (err) {
      throw err;
    }
  },

  // Buscar universidades ativas
  async getActiveUniversities() {
    try {
      const { data, error } = await supabase
        .from('universidades')
        .select('*')
        .eq('ativa', true)
        .order('nome', { ascending: true });
      
      if (error) throw error;
      return data;
    } catch (err) {
      throw err;
    }
  },

  // Adicionar universidade
  async addUniversity(universityData) {
    try {
      const { data, error } = await supabase
        .from('universidades')
        .insert([universityData])
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      throw err;
    }
  },

  // Atualizar status da universidade
  async toggleUniversityStatus(id, status) {
    try {
      const { data, error } = await supabase
        .from('universidades')
        .update({ ativa: status })
        .eq('id', id)
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      throw err;
    }
  },

  // Atualizar universidade completa
  async updateUniversity(id, universityData) {
    try {
      const { data, error } = await supabase
        .from('universidades')
        .update(universityData)
        .eq('id', id)
        .select();
      
      if (error) throw error;
      return data[0];
    } catch (err) {
      throw err;
    }
  },

  // Excluir universidade
  async deleteUniversity(id) {
    try {
      const { error } = await supabase
        .from('universidades')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return true;
    } catch (err) {
      throw err;
    }
  },

  // Verificar se domínio existe
  async checkDomain(domain) {
    try {
      const { data, error } = await supabase
        .from('universidades')
        .select('*')
        .eq('dominio', domain)
        .eq('ativa', true)
        .single();
      
      if (error && error.code !== 'PGRST116') throw error;
      return data;
    } catch (err) {
      return null;
    }
  }
};

// Funções auxiliares para compatibilidade
export const getAllUniversities = universityService.getAllUniversities;
export const getActiveUniversities = universityService.getActiveUniversities;
export const addUniversity = universityService.addUniversity;
export const updateUniversity = universityService.updateUniversity;
export const toggleUniversityStatus = universityService.toggleUniversityStatus;
export const deleteUniversity = universityService.deleteUniversity;
export const checkDomain = universityService.checkDomain;

export default {
  userService,
  postService,
  commentService,
  likeService,
  chatService,
  realtimeService,
  elogioService,
  universityService
}